// components/MasteryDashboard.js — Per-concept mastery, calm + scannable
import React, { useState, useEffect } from 'react';
import { useParams, useNavigate, Link } from 'react-router-dom';
import { motion } from 'framer-motion';
import { apiService, handleApiError } from '../services/api';
import {
  ArrowUpRight, ArrowDownRight, Minus, AlertTriangle,
  ChevronDown, ChevronRight, Flame
} from 'lucide-react';
import Loading from './common/Loading';
import usePageTitle from '../hooks/usePageTitle';
import { Button, Card, Breadcrumbs, MetricBig } from './ui';
import { MasteryGlyph } from './ui/mastery';
import { ConceptCell } from './ui/dashboard';
import { useTutor } from '../contexts/TutorRuntimeContext';

// Map mastery level → dot color (status token) + label + sort priority.
// One color cue per row, never as a background.
const LEVEL = {
  mastered:   { dot: 'good',    label: 'Mastered',   sort: 5 },
  advanced:   { dot: 'good',    label: 'Advanced',   sort: 4 },
  proficient: { dot: 'info',    label: 'Proficient', sort: 3 },
  developing: { dot: 'warn',    label: 'Developing', sort: 2 },
  struggling: { dot: 'bad',     label: 'Struggling', sort: 1 },
  novice:     { dot: 'neutral', label: 'Novice',     sort: 0 },
  untested:   { dot: 'faint',   label: 'Untested',   sort: -1 },
};

// Score-bar background per level (matches the dot color, used for the thin progress sliver).
// Single-hue mastery ramp (Field Notes): progression, never a stoplight —
// a struggling learner is LOW-FILL sienna, not red. No-ambush is structural.
const LEVEL_BAR = {
  mastered:   'bg-mastery-mastered',
  advanced:   'bg-mastery-advanced',
  proficient: 'bg-mastery-proficient',
  developing: 'bg-mastery-developing',
  struggling: 'bg-mastery-struggling',
  novice:     'bg-mastery-novice',
  untested:   'bg-mastery-track',
};

const TREND = {
  improving:         <ArrowUpRight   className="w-3.5 h-3.5 text-status-good" />,
  declining:         <ArrowDownRight className="w-3.5 h-3.5 text-status-bad" />,
  stable:            <Minus          className="w-3.5 h-3.5 text-faint" />,
  insufficient_data: null,
  no_data:           null,
};

function lastTestedLabel(days) {
  if (days === null || days === undefined) return '—';
  if (days === 0) return 'today';
  if (days === 1) return 'yesterday';
  if (days < 7) return `${days}d ago`;
  if (days < 30) return `${Math.floor(days / 7)}w ago`;
  return `${Math.floor(days / 30)}mo ago`;
}

// ---------------------------------------------------------------------------
// One concept = one row. Dense, scannable, no decoration.
// ---------------------------------------------------------------------------
function ConceptRow({ concept }) {
  const lvl = LEVEL[concept.mastery_level] || LEVEL.untested;
  const isUntested = concept.mastery_level === 'untested';
  const barClass = LEVEL_BAR[concept.mastery_level] || LEVEL_BAR.untested;

  return (
    <tr className="border-t border-line-subtle hover:bg-surface-muted transition-colors">
      {/* Concept name + level dot */}
      <td className="py-3 pr-3">
        <div className="flex items-center gap-2.5">
          <MasteryGlyph level={concept.mastery_level === 'untested' ? 'novice' : concept.mastery_level} size="sm" />
          <span className="text-sm text-ink truncate">
            {concept.concept.replace(/_/g, ' ')}
          </span>
          {concept.is_due_for_review && (
            <span className="text-xs text-status-warn">due</span>
          )}
        </div>
      </td>

      {/* Level label */}
      <td className="py-3 px-3 text-sm text-mutedink hidden sm:table-cell">
        {lvl.label}
      </td>

      {/* Score with subtle bar */}
      <td className="py-3 px-3 w-32">
        {isUntested ? (
          <span className="text-sm text-faint">—</span>
        ) : (
          <div className="flex items-center gap-2">
            <span className="text-sm tabular-nums text-ink w-7 text-right">
              {concept.mastery_score.toFixed(1)}
            </span>
            <div className="flex-1 h-1 bg-line-subtle rounded-full overflow-hidden">
              <div
                className={`h-1 ${barClass}`}
                style={{ width: `${(concept.mastery_score / 10) * 100}%` }}
              />
            </div>
          </div>
        )}
      </td>

      {/* Trend */}
      <td className="py-3 px-3 hidden md:table-cell">
        <span className="inline-flex items-center">{TREND[concept.trend]}</span>
      </td>

      {/* Attempts */}
      <td className="py-3 px-3 text-sm text-mutedink tabular-nums hidden md:table-cell">
        {concept.total_attempts || '—'}
      </td>

      {/* Last tested */}
      <td className="py-3 pl-3 text-sm text-mutedink hidden lg:table-cell">
        {lastTestedLabel(concept.days_since_last_test)}
      </td>
    </tr>
  );
}

// ---------------------------------------------------------------------------
// Domain section — collapsible, one accent per row
// ---------------------------------------------------------------------------
function DomainSection({ domain, courseId }) {
  const [expanded, setExpanded] = useState(true);
  const concepts = domain.concepts || [];
  const tested = concepts.filter(c => c.mastery_level !== 'untested');
  const avgScore = tested.length > 0
    ? tested.reduce((s, c) => s + c.mastery_score, 0) / tested.length
    : null;
  const dueCount = concepts.filter(c => c.is_due_for_review).length;

  // Sort: due first, then by mastery (struggling before mastered), then alphabetical
  const sorted = [...concepts].sort((a, b) => {
    if (a.is_due_for_review !== b.is_due_for_review) return a.is_due_for_review ? -1 : 1;
    const aLvl = (LEVEL[a.mastery_level] || LEVEL.untested).sort;
    const bLvl = (LEVEL[b.mastery_level] || LEVEL.untested).sort;
    if (aLvl !== bLvl) return aLvl - bLvl;
    return a.concept.localeCompare(b.concept);
  });

  return (
    <Card>
      {/* Header — single row, muted */}
      <button
        onClick={() => setExpanded(!expanded)}
        className="w-full px-5 py-4 flex items-center justify-between hover:bg-surface-muted transition-colors rounded-xl"
      >
        <div className="flex items-center gap-3 min-w-0">
          {expanded
            ? <ChevronDown className="w-4 h-4 text-faint flex-shrink-0" />
            : <ChevronRight className="w-4 h-4 text-faint flex-shrink-0" />
          }
          <h3 className="text-ink font-medium truncate">{domain.domain_name}</h3>
          <span className="text-sm text-faint">
            {tested.length}/{concepts.length}
          </span>
        </div>
        <div className="flex items-center gap-5 text-sm flex-shrink-0">
          {dueCount > 0 && (
            <span className="text-status-warn">{dueCount} due</span>
          )}
          {avgScore !== null && (
            <span className="text-ink tabular-nums">
              <span className="font-medium">{avgScore.toFixed(1)}</span>
              <span className="text-faint font-normal"> avg</span>
            </span>
          )}
        </div>
      </button>

      {expanded && (
        <div className="px-5 pb-5">
          <table className="w-full">
            <thead>
              <tr className="text-xs text-faint uppercase tracking-wide">
                <th className="text-left font-normal pb-2 pr-3">Concept</th>
                <th className="text-left font-normal pb-2 px-3 hidden sm:table-cell">Level</th>
                <th className="text-left font-normal pb-2 px-3">Score</th>
                <th className="text-left font-normal pb-2 px-3 hidden md:table-cell">Trend</th>
                <th className="text-left font-normal pb-2 px-3 hidden md:table-cell">Attempts</th>
                <th className="text-left font-normal pb-2 pl-3 hidden lg:table-cell">Last</th>
              </tr>
            </thead>
            <tbody>
              {sorted.map(c => <ConceptRow key={c.concept} concept={c} />)}
            </tbody>
          </table>

          <div className="mt-4 flex gap-2">
            <Button to={`/course/${courseId}/quiz/${domain.domain}`} variant="primary">
              Practice quiz
            </Button>
          </div>
        </div>
      )}
    </Card>
  );
}

// ---------------------------------------------------------------------------
// Streak — one quiet line, no widgets, no anxiety triggers
// ---------------------------------------------------------------------------
function StreakLine({ courseId }) {
  const [streak, setStreak] = useState(null);
  useEffect(() => {
    apiService.getLearningStreak(courseId).then(res => setStreak(res.data.data)).catch(() => {});
  }, [courseId]);

  if (!streak || streak.current_streak === 0) return null;

  return (
    <div className="flex items-center gap-2 text-sm text-mutedink">
      <Flame className="w-4 h-4 text-accent" />
      <span>
        <span className="text-ink font-medium">{streak.current_streak}-day</span> streak
        {streak.longest_streak > streak.current_streak && (
          <span className="text-faint"> · best {streak.longest_streak}</span>
        )}
        {streak.streak_at_risk && (
          <span className="text-status-warn"> · at risk today</span>
        )}
      </span>
    </div>
  );
}

// ---------------------------------------------------------------------------
// Main page
// ---------------------------------------------------------------------------
export default function MasteryDashboard() {
  const [view, setView] = useState('detail');
  const { sendToTutor } = useTutor();
  const { courseId } = useParams();
  const navigate = useNavigate();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  useEffect(() => {
    const fetchData = async () => {
      try {
        const res = await apiService.getMasteryDashboard(courseId);
        setData(res.data.data);
      } catch (err) {
        setError(handleApiError(err));
      } finally {
        setLoading(false);
      }
    };
    fetchData();
  }, [courseId]);

  usePageTitle(data?.course?.name ? `Mastery — ${data.course.name}` : 'Mastery');

  if (loading) return <Loading message="Loading mastery..." />;

  if (error) {
    return (
      <div className="min-h-screen bg-surface-muted flex items-center justify-center">
        <div className="text-center space-y-4">
          <AlertTriangle className="w-10 h-10 text-status-warn mx-auto" />
          <p className="text-mutedink">{error}</p>
          <button onClick={() => navigate(-1)} className="text-accent-ink hover:underline">Go back</button>
        </div>
      </div>
    );
  }

  const { summary, domains, course } = data;
  const overallScore = summary.coverage_percentage; // 0–100
  const dueText = summary.concepts_due_for_review > 0
    ? `${summary.concepts_due_for_review} due for review`
    : null;
  const strugglingText = summary.concepts_struggling > 0
    ? `${summary.concepts_struggling} struggling`
    : null;
  const masteredText = summary.concepts_mastered > 0
    ? `${summary.concepts_mastered} mastered`
    : null;

  return (
    <div className="min-h-screen bg-surface-muted">
      <div className="max-w-5xl mx-auto px-4 sm:px-6 py-10 space-y-8">

        <Breadcrumbs items={[
          { label: 'My Courses', to: '/my-courses' },
          { label: course.name, to: `/course/${courseId}` },
          { label: 'Mastery' },
        ]} />

        {/* Pass 1 — overview: one big number, two lines of context */}
        <motion.div
          initial={{ opacity: 0, y: 8 }}
          animate={{ opacity: 1, y: 0 }}
          className="space-y-2"
        >
          <MetricBig
            value={overallScore}
            suffix="%"
            caption={`of ${summary.total_concepts} concepts tested at least once`}
          />

          {/* Pass 2 — what to do, only if there's something to do */}
          {(dueText || strugglingText || masteredText) && (
            <div className="flex flex-wrap items-center gap-x-5 gap-y-1 pt-2 text-sm">
              {dueText && (
                <Link to={`/course/${courseId}/practice`} className="text-accent hover:text-accent-hover hover:underline">
                  {dueText} →
                </Link>
              )}
              {strugglingText && (
                <span className="text-status-bad">{strugglingText}</span>
              )}
              {masteredText && (
                <span className="text-status-good">{masteredText}</span>
              )}
            </div>
          )}

          <StreakLine courseId={courseId} />
        </motion.div>

        {/* Pass 3 — per-domain detail or the dense at-a-glance grid. */}
        <div className="view-tabs" style={{ background: 'transparent', borderBottom: 0, padding: 0 }}>
          <button type="button" className="view-tab" aria-pressed={view === 'detail'} style={view === 'detail' ? { color: 'var(--ink)', borderColor: 'var(--hairline-2)' } : undefined} onClick={() => setView('detail')}>Detail</button>
          <button type="button" className="view-tab" aria-pressed={view === 'grid'} style={view === 'grid' ? { color: 'var(--ink)', borderColor: 'var(--hairline-2)' } : undefined} onClick={() => setView('grid')}>Grid</button>
        </div>
        {view === 'grid' && (
          <div className="cgrid">
            {domains.flatMap((d) => (d.concepts || []).map((c) => (
              <ConceptCell
                key={`${d.domain}-${c.concept}`}
                m={{
                  concept: c.concept,
                  mastery_level: c.mastery_level,
                  mastery_score: typeof c.mastery_score === 'number' ? c.mastery_score : undefined,
                  due_for_review: c.is_due_for_review,
                }}
                onAsk={(m) => sendToTutor(`Help me with ${m.concept} in ${courseId} — where should I start?`)}
              />
            )))}
          </div>
        )}
        <div className="space-y-3">
          {view === 'detail' && domains.map(domain => (
            <DomainSection key={domain.domain} domain={domain} courseId={courseId} />
          ))}
        </div>
      </div>
    </div>
  );
}
